// ORG-03: before the shell or business setup opens, a signed-in user accepts any current platform
// document they have not accepted yet. Nothing shows while no document is published (OD-23: texts
// come from counsel). The gate is UX; the acceptance record is what the server keeps.
import { useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "@ady/i18n";
import { Banner, Button } from "@ady/ui";
import { api } from "../api/client";
import { errorMessage } from "../api/errors";
import { keys, useLegal } from "../api/queries";
import { TID } from "../testids";
import { AuthLayout } from "./AuthLayout";

export function LegalGate({ children }: { children: ReactNode }) {
  const legal = useLegal();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  if (legal.isPending) return null;
  // A failed status read does not lock the vendor out; the gate re-checks on the next load.
  if (!legal.data?.acceptance_required) return <>{children}</>;

  const pending = legal.data.documents.filter((d) => d.accepted_at === null);
  const accept = async () => {
    setBusy(true);
    setError(undefined);
    try {
      qc.setQueryData(keys.legal, await api.acceptLegal(pending.map((d) => d.id)));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={t("legal.title")} lead={t("legal.lead")} testId={TID.legalGate}>
      {error ? <Banner kind="error" title={error} data-testid={TID.formError} /> : null}
      <ul className="flex flex-col gap-3">
        {pending.map((d) => {
          const name = d.kind === "terms" ? t("legal.kinds.terms") : t("legal.kinds.privacy");
          return (
            <li key={d.id} className="flex flex-col gap-1 rounded-md border border-line p-4">
              <a
                href={d.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("legal.open", { name })}
                className="font-semibold text-link underline-offset-2 hover:underline"
              >
                {name}
              </a>
              <span className="text-sm text-fg-secondary">
                {t("legal.version", { version: d.version })}
              </span>
            </li>
          );
        })}
      </ul>
      <Button
        icon="check"
        fullWidth
        loading={busy}
        loadingLabel={t("legal.accepting")}
        onClick={accept}
        data-testid={TID.legalAccept}
      >
        {t("legal.accept")}
      </Button>
    </AuthLayout>
  );
}
